import { t } from "i18next";
import { useEffect, useState, useRef, useCallback } from "react";
import { useBeforeUnload, useBlocker } from "react-router";

type useLeaveDetectionMethods = {
    proceedWithNavigation: () => void;
    cancelNavigation: () => void;
    isBlocking: boolean;
};

export const useLeaveDetection = (shouldBlock: boolean, onLeaveAttempt: () => void): useLeaveDetectionMethods => {
    const isHandlingRef = useRef(false);
    const navigateActionRef = useRef<{
        proceed: () => void;
        reset: () => void;
    } | null>(null);

    const [isBlocking, setIsBlocking] = useState(false);

    const blocker = useBlocker(
        useCallback(
            ({ historyAction }: { historyAction: string }) => {
                if (isHandlingRef.current) return false;

                return shouldBlock;
            },
            [shouldBlock],
        ),
    );

    useEffect(() => {
        if (blocker.state === "blocked") {
            isHandlingRef.current = true;
            setIsBlocking(true);
            navigateActionRef.current = {
                proceed: () => {
                    blocker.proceed();
                },
                reset: () => {
                    blocker.reset();
                },
            };

            onLeaveAttempt();
        } else if (blocker.state === "unblocked" && isHandlingRef.current) {
            isHandlingRef.current = false;
            setIsBlocking(false);
            navigateActionRef.current = null;
        }
    }, [blocker, onLeaveAttempt]);

    useBeforeUnload(
        useCallback(
            (event) => {
                if (shouldBlock && !isHandlingRef.current) {
                    event.preventDefault();
                    {
                        t("cms.editor.quit.confirm.leave", "Leave Without Saving");
                    }
                }
            },
            [shouldBlock],
        ),
        { capture: true },
    );

    const proceedWithNavigation = useCallback(() => {
        const action = navigateActionRef.current;
        if (action) {
            navigateActionRef.current = null;
            action.proceed();

            setIsBlocking(false);
        }
    }, []);

    const cancelNavigation = useCallback(() => {
        const action = navigateActionRef.current;
        if (action) {
            navigateActionRef.current = null;
            action.reset();

            setTimeout(() => {
                isHandlingRef.current = false;
                setIsBlocking(false);
            }, 50);
        }
    }, []);

    return {
        proceedWithNavigation,
        cancelNavigation,
        isBlocking,
    };
};
